// MA-C21 — `pattern_strip` renderer for ms.math.00g pattern-continuation
// items ("What comes next?").
//
// THE GAP THIS CLOSES: these 25 pre-chapter items rendered BLANK — there was
// no pattern-strip renderer, so the authored AB/ABB/AAB/ABC sequence the
// item asks the child to continue was never drawn.
//
// WHAT IT DRAWS: ONLY the authored `sequence` (the item's `shown` tiles) + a
// "?" tile for the next (hidden) element. The mapper structurally never
// forwards the answer to this component, so it cannot reveal it even by bug.
//
// SAFETY NOTE (unlike every other math visual in this registry): pattern_strip
// has NO drawn-answer-safety invariant to enforce — ABB patterns legitimately
// repeat their last shown tile as the next answer (4/25 real items do), so
// "the answer never appears in `shown`" is not a true property of this
// corpus. Safety here is FIDELITY to the authored sequence + never drawing a
// "next" tile, not answer-absence (MA-C21 decision log / MA-216 §3b panel).
//
// ADDITIVE — registered in rendererRegistry.tsx beside the other built-ins.
import type { ReactElement } from "react";
import type { MathRendererProps } from "./rendererRegistry";
import { colorToken, shapeGlyph, parseColoredShapeToken } from "./shapeGlyphs";

/** MA-741 (capture instrument for MA-726's strip half) — one shown unit AS
 *  DRAWN: exactly the arguments this renderer hands `shapeGlyph` apart from
 *  the position. `fill` is the unit's hue; `shape`, `family` and `size` are
 *  its non-hue channels. */
export type DrawnPatternUnit = { shape: string | undefined; fill: string; family: "filled"; size: number };

/** MA-741 — the hidden `data-pattern-strip-non-hue` value, derived from the
 *  units the strip actually draws: "true" when every pair of drawn units
 *  whose fill differs also differs in a non-hue channel (shape, style family
 *  or size), "false" when some pair differs by fill alone. Units that draw no
 *  glyph (unknown shape) are not drawn and are skipped. MA-726's rule, read
 *  off the drawing inputs rather than a constant. A closed-set token only;
 *  nothing a child sees or hears changes. */
export function patternStripNonHueAttr(units: readonly DrawnPatternUnit[]): "true" | "false" {
  const drawn = units.filter((u) => typeof u.shape === "string" && u.shape.length > 0);
  for (let i = 0; i < drawn.length; i++) {
    for (let j = i + 1; j < drawn.length; j++) {
      const a = drawn[i], b = drawn[j];
      if (a.fill === b.fill) continue;
      if (a.shape === b.shape && a.family === b.family && a.size === b.size) return "false";
    }
  }
  return "true";
}

const TILE = 52;
const GAP = 14;
const PAD = 12;

export default function PatternStripRenderer({ sequence }: MathRendererProps): ReactElement {
  const shown = Array.isArray(sequence) ? sequence.filter((s) => typeof s === "string" && s.length > 0) : [];
  // MA-741 — each unit as drawn, built once and used by BOTH the glyph draw
  // below and the hidden data-pattern-strip-non-hue hook on the root <svg>.
  const units: DrawnPatternUnit[] = shown.map((token) => {
    const { shape, color } = parseColoredShapeToken(token);
    return { shape, fill: colorToken(color), family: "filled", size: 18 };
  });
  const tileCount = shown.length + 1; // + the "?" tile
  const W = Math.max(100, tileCount * TILE + (tileCount - 1) * GAP + PAD * 2);
  const H = TILE + PAD * 2;
  const cx = (i: number) => PAD + TILE / 2 + i * (TILE + GAP);
  const cy = H / 2;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ maxWidth: W }} role="img"
      aria-label={shown.length > 0 ? `Pattern: ${shown.join(", ")}, then what comes next?` : "Pattern strip"}
      data-pattern-strip-non-hue={patternStripNonHueAttr(units)}>
      {units.map((u, i) => {
        return (
          <g key={i} data-tile="shown">
            <rect x={cx(i) - TILE / 2} y={cy - TILE / 2} width={TILE} height={TILE} rx="8"
              fill="none" stroke="var(--color-border-secondary, #cdbfae)" strokeWidth="2" />
            {u.shape ? shapeGlyph(u.shape, cx(i), cy, u.size, u.fill, u.family) : null}
          </g>
        );
      })}
      <g data-tile="next">
        <rect x={cx(shown.length) - TILE / 2} y={cy - TILE / 2} width={TILE} height={TILE} rx="8"
          fill="var(--pal-cream, #faf3e8)" stroke="var(--color-border-secondary, #cdbfae)"
          strokeWidth="2" strokeDasharray="4 3" />
        <text x={cx(shown.length)} y={cy + 9} textAnchor="middle" fontSize="26" fontWeight="700"
          fontFamily="inherit" fill="var(--color-text-secondary, #7a6f60)">?</text>
      </g>
    </svg>
  );
}
